/**
 * PaymentSummarySection — calm aggregate.
 *
 * Projection shape:
 *   { total_committed, paid_so_far, balance_due, per_vendor: [...] }
 *
 * We surface three totals; no booking IDs, no payment_status enums, no
 * vendor breakdown table (the existing EventDetail already does that
 * granular view — this is a calm at-a-glance summary).
 */

import React from 'react';
import { DollarSign } from 'lucide-react';

const fmt = (n) =>
  n == null
    ? null
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(n);

export default function PaymentSummarySection({ payments }) {
  if (!payments) return null;
  const total = Number(payments.total_committed || 0);
  const paid = Number(payments.paid_so_far || 0);
  const balance = Number(payments.balance_due || 0);
  // If nothing committed yet, the section is silent.
  if (total === 0 && paid === 0 && balance === 0) return null;
  return (
    <div
      className="rounded-xl bg-white border border-gray-100 p-4"
      data-testid="ops-payments"
    >
      <div className="flex items-center gap-2 mb-2">
        <DollarSign className="w-4 h-4 text-emerald-500" aria-hidden />
        <p className="text-xs uppercase tracking-wide text-gray-400">
          Payments
        </p>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div data-testid="ops-payments-total">
          <p className="text-[11px] text-gray-400">Committed</p>
          <p className="text-sm font-semibold text-gray-900">{fmt(total)}</p>
        </div>
        <div data-testid="ops-payments-paid">
          <p className="text-[11px] text-gray-400">Paid</p>
          <p className="text-sm font-semibold text-emerald-700">{fmt(paid)}</p>
        </div>
        <div data-testid="ops-payments-balance">
          <p className="text-[11px] text-gray-400">Balance</p>
          <p
            className={`text-sm font-semibold ${
              balance > 0 ? 'text-amber-700' : 'text-gray-500'
            }`}
          >
            {fmt(balance)}
          </p>
        </div>
      </div>
    </div>
  );
}
